import { useState, useEffect, useCallback } from 'react'
import { BlogService } from '../services/blogService'
import { Comment, CreateCommentRequest } from '../config/api'

export const useComments = (slug: string | undefined) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const blogService = BlogService.getInstance()

  const fetchComments = useCallback(async () => {
    if (!slug) {
      setComments([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const commentsData = await blogService.getCommentsBySlug(slug)
      setComments(commentsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch comments')
      setComments([])
    } finally {
      setLoading(false)
    }
  }, [slug, blogService])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const createComment = useCallback(async (commentData: CreateCommentRequest): Promise<boolean> => {
    if (!slug) return false

    try {
      setSubmitting(true)
      setError(null)
      const newComment = await blogService.createComment(slug, commentData)
      
      if (newComment) {
        // Add the new comment to the list and refresh
        await fetchComments()
        return true
      } else {
        setError('Failed to submit comment')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit comment')
      return false
    } finally {
      setSubmitting(false)
    }
  }, [slug, blogService, fetchComments])

  const refresh = useCallback(() => {
    fetchComments()
  }, [fetchComments])

  return {
    comments,
    loading,
    error,
    submitting,
    createComment,
    refresh,
  }
} 